/* eslint-disable no-nested-ternary */
import { useMemo, useState, useCallback } from 'react';
import { capitalCase } from 'change-case';
import { useTranslation } from 'react-i18next';
import { Alert, Snackbar } from '@mui/material';
import { useAppSelector } from '../../../store/hooks';
import {
  ResetTopic1ContentList,
  selectTopic1ContentPageNumber,
  selectGetTopic1ContentState,
  selectTopic1HasMoreContent,
  selectTopic1ContentList,
  UpdateTopic1HasMoreContent,
  UpdateTopic1PageNumber,
} from '../../../store/slices/contentSlice';
import { getTopic1Content } from '../../../store/thunks/contentThunk';
import ContentHomeSkeleton from './ContentHomeSkeleton';
import { ContentCategoryContainer } from './ContentCategoryContainer';
import { useTrackCategoryContentVisibility } from './useTrackCategoryContentVisibility';
import { useFetchCategoryContent } from './useFetchCategoryContent';
import { Content, ContentFeedSection } from '../../../common/models/content/Content';
import ContentViewMoreContainer from './ContentViewMoreContainer';
import ContentViewMoreSkeleton from './ContentViewMoreSkeleton';

interface Topic1ContentCategoryProps {
  categoryId: string;
  topicName: string;
  setSomeContentLoadedInitially: (value: boolean) => void;
  contentFeedSection: ContentFeedSection
  viewMorePage?: boolean
}

export function Topic1ContentCategory({
  categoryId,
  topicName,
  viewMorePage = false,
  setSomeContentLoadedInitially,
  contentFeedSection,
}:Topic1ContentCategoryProps) {
  const storeTopic1ContentList = useAppSelector(selectTopic1ContentList);
  const topic1ContentList = useMemo(() => storeTopic1ContentList.map((content, indx) => (
    {
      ...content,
      contentFeedSection,
      contentFeedSectionPosition: indx + 1,
    } as Content
  )), [storeTopic1ContentList, contentFeedSection]);

  const getTopic1ContentState = useAppSelector(selectGetTopic1ContentState);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const { t } = useTranslation();
  const topic1Page = useAppSelector(selectTopic1ContentPageNumber);
  const topic1HasMore = useAppSelector(selectTopic1HasMoreContent);
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  useTrackCategoryContentVisibility({
    categoryContentList: topic1ContentList,
  });

  const fetchMoreCategoryContent = useFetchCategoryContent({
    viewMorePage,
    contentRequestOptions: { contentTopics: topicName },
    getCategoryContent: getTopic1Content,
    resetCategoryContentList: ResetTopic1ContentList,
    setSomeContentLoadedInitially,
    setOpenErrorSnackbar,
    setInitialFetchDone,
    updateCategoryPageNumber: UpdateTopic1PageNumber,
    updateHasMoreCategoryContent: UpdateTopic1HasMoreContent,
  });

  const isLoading = useMemo(
    () => (getTopic1ContentState === 'pending' && !initialFetchDone),
    [getTopic1ContentState, initialFetchDone],
  );

  const handleCloseErrorSnackBar = () => {
    setOpenErrorSnackbar(false);
  };

  const onNextPage = useCallback(() => {
    fetchMoreCategoryContent(
      topic1Page,
      topic1HasMore,
    );
  }, [topic1Page, topic1HasMore, fetchMoreCategoryContent]);

  return (
    <>
      <Snackbar open={openErrorSnackbar} autoHideDuration={4000} onClose={handleCloseErrorSnackBar}>
        <Alert severity="error" sx={{ width: '100%' }}>
          {t('error.generalMessage')}
        </Alert>
      </Snackbar>

      {isLoading ? (
        viewMorePage ? (
          <ContentViewMoreSkeleton />
        ) : (
          <ContentHomeSkeleton key={`${topicName}-skeleton`} />
        )
      ) : viewMorePage ? (
        <ContentViewMoreContainer
          key={`${categoryId}-content`}
          categoryId={categoryId}
          storeContentList={topic1ContentList}
          onNextPage={onNextPage}
          hasMoreContent={topic1HasMore}
          showSearchBar={true}
        />
      ) : topic1ContentList?.length > 0 ? (
        <ContentCategoryContainer
          categoryId={categoryId}
          key={`${topicName}-content`}
          categoryTitle={capitalCase(topicName)}
          storeContentList={topic1ContentList}
          contentFeedSection={contentFeedSection}
        />
      ) : null}
    </>
  );
}
